'use client';
import SummaryLine from '@components/Cart/SummaryLine';
import { useCurrencyString } from '@hooks/useCurrencyString';
import { Order } from '@repo/ui/src/types/order';
import React from 'react';
import useTranslation from '@repo/ui/src/context/Translation/useTranslation';

export interface OrderSummaryCartProps {
  orderDetails: Order['details'];
}

const OrderDetailCostSummary = ({
  orderDetails: {
    currency,
    orderTotal,
    taxTotal,
    shippingTotal,
    subTotal,
    productItems,
    giftCertificateItems,
  },
}: OrderSummaryCartProps) => {
  const getCurrencyString = useCurrencyString(currency);
  const { t } = useTranslation();
  const totalItems =
    (giftCertificateItems?.length ?? 0) +
    (productItems?.reduce((acc, line) => acc + line.quantity, 0) ?? 0);

  const item = t('orderSummary.item', { count: totalItems }, { default: 'item' });

  return (
    <div className="tw-grid tw-self-stretch tw-grid-rows-[auto_1fr]">
      <h5 className="tra-text-web-heading-5 tw-mb-5">
        {t('orderSummary.orderSummaryTitle', undefined, {
          default: 'Order Summary',
        })}
      </h5>
      <div className="tw-grid tw-content-start tw-gap-y-4 tw-p-6 tw-bg-warm-100 tw-border tw-border-warm-300">
        <div className="tw-gap-y-2 tw-grid">
          <p className="tra-text-web-body-m tw-font-bold tw-text-neutral-400">
            <span>{`${totalItems} ${item} ${getCurrencyString(orderTotal)}`}</span>
          </p>
        </div>
        <hr />
        <div className="tw-grid tw-gap-y-2">
          <SummaryLine
            key="subtotal"
            title={t(
              'orderSummary.subtotalItems',
              {
                totalItems: totalItems,
              },
              { default: `Subtotal ({{totalItems}} items) ` },
            )}
            value={subTotal}
            currency={currency}
          />
          <SummaryLine
            key="key"
            title={t('orderSummary.tax', undefined, {
              default: 'Tax',
            })}
            value={taxTotal}
            currency={currency}
          />
          <SummaryLine
            key="shipping"
            title={t('cart.shipping', undefined, {
              default: 'Shipping',
            })}
            value={shippingTotal}
            currency={currency}
          />
          <hr />
          <SummaryLine
            key="total"
            title={t('orderSummary.orderTotal', undefined, {
              default: 'Order Total',
            })}
            value={orderTotal}
            bold
            currency={currency}
          />
        </div>
      </div>
    </div>
  );
};

export default OrderDetailCostSummary;
